import useGetConnectors from 'hooks/Bank/useGetConnectors';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Connector } from 'types/transactions.types';
import {
  isMainAccountableBank,
  isTaxReserveAccountableBank,
} from '../../helpers';

export const useAccountOptions = (
  sourceAccount?: Connector,
  isStandingOrderPayment?: boolean,
) => {
  const { t } = useTranslation();
  const { data: connectors } = useGetConnectors();

  const accountableBanks = useMemo(() => {
    return (
      (connectors as Connector[])?.filter(
        (connector) => connector.provider === 'swan',
      ) || []
    );
  }, [connectors]);

  // Filter source accounts (exclude disabled ones for standing order payments)
  const sourceAccountOptions = useMemo(() => {
    const filteredBanks = accountableBanks
      .filter(isMainAccountableBank)
      .concat(accountableBanks.filter((bank) => !isMainAccountableBank(bank)))
      .filter(
        (bank) =>
          !(isStandingOrderPayment && isTaxReserveAccountableBank(bank)),
      );

    return filteredBanks.map((connector) => ({
      ...connector,
      value: connector._id,
      label: connector.title,
    }));
  }, [accountableBanks, isStandingOrderPayment]);

  // Filter recipient accounts (exclude source account)
  const receiptAccountOptions = useMemo(() => {
    const externalOption = {
      value: 'external',
      label: t('accountable_banking.send_money.external'),
    };

    const internalOptions = accountableBanks
      .filter((connector) => connector._id !== sourceAccount?._id)
      .map((connector) => ({
        ...connector,
        value: connector._id,
        label: connector.title,
      }));

    return [externalOption, ...internalOptions];
  }, [accountableBanks, sourceAccount, t]);

  return {
    accountableBanks,
    sourceAccountOptions,
    receiptAccountOptions,
  };
};
